import { useCallback, useEffect, useState, useRef } from 'react'
import { getKyousouApiClient } from '../services/network'
import { PartnerRecruitment } from '../clients/kyousou_api_client'
import {
  RecruitmentSortType,
  RecruitmentSortTypeValue,
} from '../types/recruitment'

/**
 * パートナー募集（共創パートナー募集）一覧を取得するフック（無限スクロール対応）
 */
export const useOwnRecruitment = (
  limit = 8,
  initialSort: RecruitmentSortTypeValue = RecruitmentSortType.CREATED_AT
): {
  totalCount: number
  cursor: number
  isLoading: boolean
  isLoadingMore: boolean
  hasMore: boolean
  recruitments: PartnerRecruitment[]
  loadMore: () => void
  changeSort: (newSort: RecruitmentSortTypeValue) => void
  currentSort: RecruitmentSortTypeValue
} => {
  // APIクライアントを取得
  const apiClient = getKyousouApiClient()

  // 状態管理
  const [totalCount, setTotalCount] = useState(0) // 総件数
  const [cursor, setCursor] = useState(0) // 現在の読み込み位置
  const [isLoading, setIsLoading] = useState(true) // 初回読み込み中フラグ
  const [isLoadingMore, setIsLoadingMore] = useState(false) // 追加読み込み中フラグ
  const [recruitments, setRecruitments] = useState<PartnerRecruitment[]>([]) // パートナー募集一覧
  const [currentSort, setCurrentSort] = useState(initialSort) // 現在のソート順
  const abortControllerRef = useRef<AbortController | null>(null) // リクエストキャンセル用
  const hasMore = cursor < totalCount // まだ読み込めるデータがあるかどうか

  // データ読み込み関数（初回・追加読み込み対応）
  const load = useCallback(
    async (isLoadMore = false) => {
      // 既存のリクエストをキャンセル
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
      abortControllerRef.current = new AbortController()

      // ローディング状態を設定
      if (isLoadMore) {
        setIsLoadingMore(true)
      } else {
        setIsLoading(true)
        setCursor(0)
      }

      try {
        // ページベースのAPIに変換してリクエスト
        const response =
          await apiClient.partnerRecruitments.partnerRecruitmentsList({
            cursor: isLoadMore ? cursor : 0,
            limit: limit,
            sort: currentSort,
          })

        const recruitmentData = response.data.partnerRecruitments || []

        // レスポンスデータで状態を更新
        if (!abortControllerRef.current?.signal.aborted) {
          setRecruitments((prevRecruitments) => {
            if (!isLoadMore) return recruitmentData
            return [...prevRecruitments, ...recruitmentData]
          })

          setCursor((prev) =>
            isLoadMore ? prev + recruitmentData.length : recruitmentData.length
          )
          // フロントエンドで管理: 取得したアイテム数が期待値より少なければ終了
          const isLastPage = recruitmentData.length < limit
          if (isLastPage) {
            setTotalCount(cursor + recruitmentData.length)
          } else {
            setTotalCount(cursor + recruitmentData.length + 1)
          }
        }
      } catch (error) {
        if (!abortControllerRef.current?.signal.aborted) {
          console.error('パートナー募集の取得に失敗しました:', error)
          if (!isLoadMore) {
            setRecruitments([])
            setTotalCount(0)
            setCursor(0)
          }
        }
      } finally {
        if (!abortControllerRef.current?.signal.aborted) {
          if (isLoadMore) {
            setIsLoadingMore(false)
          } else {
            setIsLoading(false)
          }
        }
      }
    },
    [limit, currentSort, cursor]
  )

  // ソート条件が変更されたら初回読み込みを実行
  useEffect(() => {
    load(false)

    return () => {
      abortControllerRef.current?.abort()
    }
  }, [currentSort])

  // 追加読み込み関数
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore || isLoading) return
    load(true)
  }, [isLoadingMore, hasMore, isLoading, load])

  // ソート変更関数
  const changeSort = useCallback(
    (newSort: RecruitmentSortTypeValue) => {
      if (newSort !== currentSort) {
        setCurrentSort(newSort)
        setRecruitments([])
        setTotalCount(0)
        setCursor(0)
      }
    },
    [currentSort]
  )

  return {
    totalCount,
    cursor,
    isLoading,
    isLoadingMore,
    hasMore,
    recruitments,
    loadMore,
    changeSort,
    currentSort,
  }
}
